import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useUpdateOpenRouterKeyMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { AdminFormPage } from "@components/CatalogIndex";
import { OpenRouterKeyForm } from "../common/Form";
import { INITIAL_FORM_STATE, OpenRouterKeyFormState } from "../common/types";
import { formatOpenRouterKeyData } from "../common/helpers";
import { useGetOpenRouterKey } from "../common/api";
import "../style.css";

const FORM_ID = "open-router-key-form";

const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const keyId = id || "";
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateOpenRouterKey] = useUpdateOpenRouterKeyMutation();

  const {
    openRouterKey,
    isLoading: isFetching,
    refetch,
  } = useGetOpenRouterKey(keyId);

  const [formState, setFormState] =
    useState<OpenRouterKeyFormState>(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  useEffect(() => {
    if (openRouterKey && keyId) {
      setFormState({ ...openRouterKey });
    }
  }, [openRouterKey, keyId]);

  const handleSave = () => {
    if (!formState.keyType) return;

    setLoading(true);
    const data = formatOpenRouterKeyData(formState);

    updateOpenRouterKey({
      slug: accountSlug,
      id: keyId as unknown as number,
      body: { data },
    })
      .unwrap()
      .then(() => {
        setSuccessMessage("Private LLM key updated successfully!");
        setShowSuccessAlert(true);
        setTimeout(() => {
          navigate("/admin/open_router_keys", {
            state: { refetch: true },
          });
        }, 500);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setErrorMessage(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
        setShowFailAlert(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (isFetching) {
    return <SmallSpinner />;
  }

  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <AdminFormPage
        title="Edit Private LLM Key"
        formId={FORM_ID}
        onCancel={() =>
          navigate("/admin/open_router_keys", { state: { refetch: true } })
        }
        cancelLabel="Back"
        isSubmitting={loading}
      >
        <OpenRouterKeyForm
          formId={FORM_ID}
          formState={formState}
          setFormState={setFormState}
          editing={true}
          onSubmit={handleSave}
        />
      </AdminFormPage>
    </>
  );
};

export default Edit;
